import React, { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  GENERIC_PAYMENT_ERROR,
  PAYMENT_METHODS,
  PAYMENT_PROVIDER,
} from '../../constants';
import { payCheck } from '../../../../store/slices/payment/paymentThunk';
import Check from '../check/check';
import StripeElement from '../../stripe/StripeElement';
import apiAccess from '../../../../api/apiAccess';
import { XpressBillPay } from '../../xpressbillpay';
import { RedirectBasedPayment } from './RedirectBasedPayment';
import { PaymentFees, PaymentMethod } from 'src/types/payment';
import { useAppDispatch } from '../../../../store/store';
import { calculateTotalDue, isGeneric } from '../utils';
import { handlePostRedirect } from './customHandlers';

export interface PaymentFormProps {
  fees: PaymentFees;
  flow: string;
  onSuccess: (isGeneric?: boolean) => void;
  paymentID: string;
  paymentMethod: PaymentMethod;
  permitIdentifier: string;
  provider: PAYMENT_PROVIDER;
  signatoryName: string;
  userEmail: string;
}

export const PaymentForm = ({
  fees,
  flow,
  onSuccess,
  paymentID,
  paymentMethod,
  permitIdentifier,
  provider,
  signatoryName,
  userEmail,
}: PaymentFormProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const dispatch = useAppDispatch();

  const isGenericProvider = useMemo(() => isGeneric(provider), [provider]);

  const getPaymentApi = (
    paymentMethod: PaymentMethod,
    provider: PAYMENT_PROVIDER
  ) => {
    switch (paymentMethod) {
      case PAYMENT_METHODS.CHECK:
        return async () => dispatch(payCheck());
      case PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD:
        if (provider === PAYMENT_PROVIDER.STRIPE) {
          return apiAccess.payByCreditCard;
        }
        return apiAccess.payGeneric;
      case PAYMENT_METHODS.ACH_DIRECT_DEBIT:
        if (provider === PAYMENT_PROVIDER.STRIPE) {
          return apiAccess.payByACHDirectDebit;
        }
        return apiAccess.payGeneric;
      default:
        return undefined;
    }
  };

  const totalDue = useMemo(
    () => calculateTotalDue(paymentMethod, fees, isGenericProvider),
    [fees, paymentMethod, isGenericProvider]
  );

  const handlePayment = async () => {
    setLoading(true);
    const paymentApi = getPaymentApi(paymentMethod, provider);
    try {
      if (paymentApi) {
        const response = await paymentApi(paymentID, signatoryName, userEmail);
        onSuccess(isGenericProvider && paymentMethod !== PAYMENT_METHODS.CHECK);
        return response;
      }
      setError(GENERIC_PAYMENT_ERROR);
    } catch (err) {
      setError(GENERIC_PAYMENT_ERROR);
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenericPayment = async () => {
    const response = await handlePayment();
    if (response?.checkout_mode === 'post_redirect') {
      handlePostRedirect(response, provider);
    } else if (response?.url?.startsWith('http')) {
      window.open(response.url, '_blank', 'toolbar=0,location=0,menubar=0');
    }
    return response;
  };

  if (paymentMethod === PAYMENT_METHODS.CHECK) {
    return (
      <Check
        onPay={handlePayment}
        loading={loading}
        error={error}
      />
    );
  } else if (provider === PAYMENT_PROVIDER.STRIPE) {
    return (
      <StripeElement
        flow={flow}
        onPay={handlePayment}
        paymentMethod={paymentMethod}
      />
    );
  } else if (provider === PAYMENT_PROVIDER.XPRESS_BILL_PAY) {
    return (
      <XpressBillPay
        onPay={handlePayment}
        permitIdentifier={permitIdentifier}
        totalDue={totalDue}
        loading={loading}
        error={error}
      />
    );
  } else if (isGenericProvider) {
    return (
      <RedirectBasedPayment
        dataTestId="generic-form-component"
        error={error}
        loading={loading}
        onClickCallback={handleGenericPayment}
        totalDue={totalDue}
      >
        The button below will take you to our third-party payment system to make
        your payment.
      </RedirectBasedPayment>
    );
  }

  return <></>;
};
